import { RainfallStation } from "./types";
import { projectToUtm21S } from "./projection";

interface ProjectedSample {
  xKm: number;
  yKm: number;
  value: number;
}

export interface ThinPlateSplineModel {
  samples: ProjectedSample[];
  radialWeights: number[];
  polynomialWeights: [number, number, number];
  originX: number;
  originY: number;
  minimum: number;
  maximum: number;
}

function projectToKilometers(
  lat: number,
  lng: number,
  originX: number,
  originY: number,
): { xKm: number; yKm: number } {
  const projected = projectToUtm21S(lat, lng);
  return {
    xKm: (projected.x - originX) / 1000,
    yKm: (projected.y - originY) / 1000,
  };
}

function thinPlateKernel(distance: number): number {
  if (distance <= 1e-12) {
    return 0;
  }
  const squared = distance * distance;
  return squared * Math.log(distance);
}

function solveLinearSystem(
  matrix: number[][],
  rightHandSide: number[],
): number[] {
  const size = rightHandSide.length;
  const augmented = matrix.map((row, index) => [...row, rightHandSide[index]]);

  for (let column = 0; column < size; column += 1) {
    let pivot = column;
    for (let row = column + 1; row < size; row += 1) {
      if (
        Math.abs(augmented[row][column]) > Math.abs(augmented[pivot][column])
      ) {
        pivot = row;
      }
    }

    if (Math.abs(augmented[pivot][column]) < 1e-12) {
      throw new Error("Singular thin-plate spline system.");
    }

    if (pivot !== column) {
      const current = augmented[column];
      augmented[column] = augmented[pivot];
      augmented[pivot] = current;
    }

    const pivotValue = augmented[column][column];
    for (let index = column; index <= size; index += 1) {
      augmented[column][index] /= pivotValue;
    }

    for (let row = 0; row < size; row += 1) {
      if (row === column) {
        continue;
      }
      const factor = augmented[row][column];
      if (Math.abs(factor) < 1e-15) {
        continue;
      }
      for (let index = column; index <= size; index += 1) {
        augmented[row][index] -= factor * augmented[column][index];
      }
    }
  }

  return augmented.map((row) => row[size]);
}

export function buildThinPlateSpline(
  stations: RainfallStation[],
  smoothing = 0.8,
): ThinPlateSplineModel | null {
  if (stations.length < 4) {
    return null;
  }

  const projectedStations = stations.map((station) =>
    projectToUtm21S(station.lat, station.lng),
  );
  const originX =
    projectedStations.reduce((sum, point) => sum + point.x, 0) /
    projectedStations.length;
  const originY =
    projectedStations.reduce((sum, point) => sum + point.y, 0) /
    projectedStations.length;
  const samples = stations.map((station) => ({
    ...projectToKilometers(station.lat, station.lng, originX, originY),
    value: station.precipitacaoMm,
  }));
  const count = samples.length;
  const size = count + 3;
  const matrix = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => 0),
  );
  const rightHandSide = Array.from({ length: size }, () => 0);

  for (let row = 0; row < count; row += 1) {
    rightHandSide[row] = samples[row].value;
    for (let column = 0; column < count; column += 1) {
      const dx = samples[row].xKm - samples[column].xKm;
      const dy = samples[row].yKm - samples[column].yKm;
      matrix[row][column] =
        thinPlateKernel(Math.sqrt(dx * dx + dy * dy)) +
        (row === column ? smoothing : 0);
    }
    matrix[row][count] = 1;
    matrix[row][count + 1] = samples[row].xKm;
    matrix[row][count + 2] = samples[row].yKm;
    matrix[count][row] = 1;
    matrix[count + 1][row] = samples[row].xKm;
    matrix[count + 2][row] = samples[row].yKm;
  }

  try {
    const solution = solveLinearSystem(matrix, rightHandSide);
    return {
      samples,
      radialWeights: solution.slice(0, count),
      polynomialWeights: [
        solution[count],
        solution[count + 1],
        solution[count + 2],
      ],
      originX,
      originY,
      minimum: Math.min(...stations.map((station) => station.precipitacaoMm)),
      maximum: Math.max(...stations.map((station) => station.precipitacaoMm)),
    };
  } catch {
    return null;
  }
}

export function evaluateThinPlateSplineAtKm(
  model: ThinPlateSplineModel,
  xKm: number,
  yKm: number,
): number {
  let result =
    model.polynomialWeights[0] +
    model.polynomialWeights[1] * xKm +
    model.polynomialWeights[2] * yKm;

  for (let index = 0; index < model.samples.length; index += 1) {
    const sample = model.samples[index];
    const dx = xKm - sample.xKm;
    const dy = yKm - sample.yKm;
    result +=
      model.radialWeights[index] *
      thinPlateKernel(Math.sqrt(dx * dx + dy * dy));
  }

  return Math.max(model.minimum, Math.min(model.maximum, result));
}

export function evaluateThinPlateSpline(
  model: ThinPlateSplineModel,
  lat: number,
  lng: number,
): number {
  const point = projectToKilometers(lat, lng, model.originX, model.originY);
  return evaluateThinPlateSplineAtKm(model, point.xKm, point.yKm);
}

export function interpolateProjectedIdwFromMeters(
  projectedStations: Array<{ x: number; y: number; value: number }>,
  queryX: number,
  queryY: number,
  power: number,
): number {
  let numerator = 0;
  let denominator = 0;
  for (let index = 0; index < projectedStations.length; index += 1) {
    const sample = projectedStations[index];
    const distance = Math.max(
      1,
      Math.hypot(queryX - sample.x, queryY - sample.y),
    );
    const weight = 1 / Math.pow(distance, power);
    numerator += weight * sample.value;
    denominator += weight;
  }
  return denominator > 0 ? numerator / denominator : Number.NaN;
}

export function interpolateProjectedIdw(
  stations: RainfallStation[],
  lat: number,
  lng: number,
  power: number,
): number {
  const query = projectToUtm21S(lat, lng);
  const projectedStations = stations.map((station) => {
    const sample = projectToUtm21S(station.lat, station.lng);
    return {
      x: sample.x,
      y: sample.y,
      value: station.precipitacaoMm,
    };
  });
  return interpolateProjectedIdwFromMeters(
    projectedStations,
    query.x,
    query.y,
    power,
  );
}

export function gaussianBlurValues(
  values: Float32Array,
  width: number,
  height: number,
  sigma: number,
): Float32Array {
  if (!(sigma > 0)) {
    return values;
  }

  const radius = Math.max(1, Math.ceil(sigma * 3));
  const kernel = new Float64Array(radius * 2 + 1);
  let kernelSum = 0;
  for (let index = -radius; index <= radius; index += 1) {
    const weight = Math.exp(-(index * index) / (2 * sigma * sigma));
    kernel[index + radius] = weight;
    kernelSum += weight;
  }
  for (let index = 0; index < kernel.length; index += 1) {
    kernel[index] /= kernelSum;
  }

  const horizontal = new Float32Array(values.length);
  const output = new Float32Array(values.length);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let sum = 0;
      for (let offset = -radius; offset <= radius; offset += 1) {
        const sampleX = Math.max(0, Math.min(width - 1, x + offset));
        sum += values[y * width + sampleX] * kernel[offset + radius];
      }
      horizontal[y * width + x] = sum;
    }
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let sum = 0;
      for (let offset = -radius; offset <= radius; offset += 1) {
        const sampleY = Math.max(0, Math.min(height - 1, y + offset));
        sum += horizontal[sampleY * width + x] * kernel[offset + radius];
      }
      output[y * width + x] = sum;
    }
  }

  return output;
}
